import StackedAreaView, { config as stackedAreaConfig } from 'app/components/dataviews/views/StackedArea';
import LineView, { config as lineConfig } from 'app/components/dataviews/views/Line';
import StackedAreaHundredPercentView, {
  config as stackedAreaHundredPercentConfig
} from 'app/components/dataviews/views/StackedAreaHundredPercent';
import StackedColumnView, { config as stackedColumnConfig } from 'app/components/dataviews/views/StackedColumn';
import BarView, { config as barConfig } from 'app/components/dataviews/views/Bar';
import PieView, { config as pieConfig } from 'app/components/dataviews/views/Pie';
import SankeyView, { config as sankeyConfig } from 'app/components/dataviews/views/Sankey';
import SunburstView, { config as sunburstConfig } from 'app/components/dataviews/views/Sunburst';
import MatrixView, { config as matrixConfig } from 'app/components/dataviews/views/Matrix';
import GaugeView, { config as gaugeConfig } from 'app/components/dataviews/views/Gauge';
import LegendTableView, { config as legendTableConfig } from 'app/components/dataviews/views/legend/Legend';
import HorizonView, { config as horizonConfig } from 'app/components/dataviews/views/Horizon';
import GeoHeatMapView, { config as geoHeatMapConfig } from 'app/components/dataviews/views/geoHeatMap';
import RawFlow, { config as rawFlowConfig } from 'app/components/dataviews/views/RawFlow/RawFlow';
import GeneratorView, { config as generatorConfig } from 'app/components/dataviews/views/GeneratorView';
import MetricsExplorerPanel from 'app/components/dataviews/views/metrics/MetricsExplorerPanel';
import type { ComponentType, ReactNode } from 'react';
import { Query } from '@kentik/ui-shared/query/query.types';

import { getEnumHealthyValue } from '@kentik/ui-shared/query/enumUtils';

export const dataviewCategories = [
  'measurement',
  'time',
  'comparison',
  'hierarchical',
  'heatmap',
  'tables',
  undefined
] as const;
export const dataviewCategoryTitles = [
  'Measurement-Specific',
  'Time-Based',
  'Comparative',
  'Hierarchical',
  'Density-Based',
  'Table'
] as const;

const baseCompatibleTypes = ['bar', 'pie', 'sankey', 'sunburst', 'gauge', 'table'] as const;
const additionalCompatibleTypes = [
  'stackedArea',
  'stackedAreaHundredPercent',
  'line',
  'stackedBar',
  'horizon'
] as const;

type Category = (typeof dataviewCategories)[number];

export type DataViewConfig<P> = {
  category?: Category | undefined;
  label: string;
  icon?: string;
  supportsPeriodOverPeriod?: boolean;
  hideFromViewMenu?: boolean;
  hideInMkp?: boolean;
  isMap?: boolean;
} & (
  | {
      udeOnly?: false;
      component: ((props: P) => ReactNode) | ComponentType<P>;
      config: object;
      units?: never;
      requirements?: never;
    }
  | {
      // New visualizations that are restricted to UDE and do not have legacy-style components available
      udeOnly: true;
      component?: never;
      config?: never;
      units?: string[];
      requirements?: (query: Query) => { title: string; description?: string } | undefined;
    }
);

export type DataViewType =
  | 'stackedArea'
  | 'stackedAreaDeep'
  | 'stackedAreaHundredPercent'
  | 'line'
  | 'stackedBar'
  | 'horizon'
  | 'bar'
  | 'pie'
  | 'sankey'
  | 'sunburst'
  | 'matrix'
  | 'gauge'
  | 'table'
  | 'geoHeatMap'
  | 'rawFlow'
  | 'generator'
  | 'metrics'
  | 'timelineView';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const dataViewTypes: Partial<Record<string, DataViewConfig<any>>> = {
  stackedArea: {
    category: 'time',
    label: 'Stacked Area Chart',
    icon: 'timeline-area-chart',
    component: StackedAreaView,
    config: stackedAreaConfig,
    supportsPeriodOverPeriod: true
  },
  stackedAreaDeep: {
    category: 'time',
    label: 'Stacked Area Chart',
    icon: 'timeline-area-chart',
    component: StackedAreaView,
    config: stackedAreaConfig,
    hideFromViewMenu: true
  },
  stackedAreaHundredPercent: {
    category: 'time',
    label: '100% Stacked Area Chart',
    icon: 'full-stacked-chart',
    component: StackedAreaHundredPercentView,
    config: stackedAreaHundredPercentConfig
  },
  line: {
    category: 'time',
    label: 'Line Chart',
    icon: 'timeline-line-chart',
    component: LineView,
    config: lineConfig,
    supportsPeriodOverPeriod: true
  },
  stackedBar: {
    category: 'time',
    label: 'Stacked Column Chart',
    icon: 'stacked-chart',
    component: StackedColumnView,
    config: stackedColumnConfig,
    supportsPeriodOverPeriod: true
  },
  horizon: {
    category: 'time',
    label: 'Horizon Chart',
    icon: 'align-justify',
    component: HorizonView,
    config: horizonConfig
  },
  bar: {
    category: 'comparison',
    label: 'Bar Chart',
    icon: 'horizontal-bar-chart',
    component: BarView,
    config: barConfig
  },
  pie: {
    category: 'comparison',
    label: 'Pie Chart',
    icon: 'doughnut-chart',
    component: PieView,
    config: pieConfig
  },
  sankey: {
    category: 'hierarchical',
    label: 'Sankey',
    icon: 'flows',
    component: SankeyView,
    config: sankeyConfig
  },
  sunburst: {
    category: 'hierarchical',
    label: 'Sunburst',
    icon: 'flash',
    component: SunburstView,
    config: sunburstConfig
  },
  matrix: {
    category: 'heatmap',
    label: 'Matrix',
    icon: 'heat-grid',
    component: MatrixView,
    config: matrixConfig,
    hideInMkp: true
  },
  gauge: {
    category: 'heatmap',
    label: 'Gauge',
    icon: 'selection',
    component: GaugeView,
    config: gaugeConfig
  },
  table: {
    category: 'tables',
    label: 'Table',
    icon: 'th-list',
    component: LegendTableView,
    config: legendTableConfig
  },
  geoHeatMap: {
    category: 'heatmap',
    label: 'Geo HeatMap',
    icon: 'globe',
    isMap: true,
    component: GeoHeatMapView,
    config: geoHeatMapConfig
  },
  rawFlow: {
    label: 'Raw Flow',
    icon: 'globe',
    component: RawFlow,
    config: rawFlowConfig,
    hideFromViewMenu: true
  },
  generator: {
    label: '',
    icon: 'grid-view',
    component: GeneratorView,
    config: generatorConfig,
    hideFromViewMenu: true
  },
  metrics: {
    label: 'Metrics',
    component: MetricsExplorerPanel,
    config: {},
    hideFromViewMenu: true
  },
  timelineView: {
    label: 'Timeline',
    icon: 'flow-linear',
    category: 'measurement',
    udeOnly: true,
    requirements(query: Query) {
      if (!query.dimensions.length) {
        return { title: 'Timeline view requires at least one dimension.' };
      }
      if (!query.metrics?.length) {
        return { title: 'Timeline view requires at least one metric.' };
      }
      // Conservative compatibility check: allow timeline only when at least one
      // metric is known to be status-like (in the HealthyValue fallback map) or
      // has a string/categorical type.  Full schema-based enforcement happens in
      // the chart-type picker (UdeChartSettingsForm) which has access to the
      // measurement model.  This guard prevents non-enum metrics on saved panels
      // from silently rendering as an unusable timeline chart.
      const metricsTypedAsString = query.metrics.filter((m) => m.type === 'string');
      const metricsWithKnownHealthyValue = query.metrics.filter(
        (m) => getEnumHealthyValue(undefined, m.name) !== undefined
      );
      if (!metricsTypedAsString.length && !metricsWithKnownHealthyValue.length) {
        return {
          title: 'Timeline view is only available for status-style metrics.',
          description: 'Select a metric like Operational Status, Session State, or Admin Status to use this view.'
        };
      }
      return undefined;
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const viewTypeOptions: (Omit<DataViewConfig<any>, 'category'> & {
  category?: (typeof dataviewCategoryTitles)[number];
  value: DataViewType;
})[] = Object.keys(dataViewTypes).map((viz_type) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const viewType = (dataViewTypes as Record<DataViewType, DataViewConfig<any>>)[viz_type as DataViewType];
  return {
    value: viz_type as DataViewType,
    ...viewType,
    category: viewType?.category && dataviewCategoryTitles[dataviewCategories.indexOf(viewType.category)]
  };
});

interface DataView {
  el?: HTMLElement;
}

interface CollectionType {
  get: () => {
    get: {
      // Signature 1: value is provided -> return string
      (value: string): string;
      // Signature 2: value is missing -> return object
      (): { view_name: string; view_description: string };
    };
  }[];
}

class DataViewStore {
  renderedDataviews: DataView[] = [];

  collection?: CollectionType;

  register(dataview: DataView) {
    this.renderedDataviews.push(dataview);
  }

  unregister(dataview: DataView) {
    this.renderedDataviews.splice(
      this.renderedDataviews.findIndex((dv) => dv === dataview),
      1
    );
  }

  getDataviewComponentByDomId(domId: string) {
    return this.renderedDataviews.find((component) => domId === component?.el?.id);
  }

  hasViewModel(type: string) {
    return !!dataViewTypes[type];
  }

  getOption(type: string) {
    return dataViewTypes[type];
  }

  getConfig(type: string) {
    return dataViewTypes[type] && dataViewTypes[type].config;
  }

  getIcon(type: string) {
    return dataViewTypes[type] && dataViewTypes[type].icon;
  }

  getViewDisplayLabel(type: string) {
    return dataViewTypes[type] && dataViewTypes[type].label;
  }

  getComponent(type: string) {
    return dataViewTypes[type]?.component;
  }

  get categories() {
    return dataviewCategories;
  }

  get categoryTitles() {
    return dataviewCategoryTitles;
  }

  get viewTypeOptions() {
    return viewTypeOptions;
  }

  getViewType(type: DataViewType) {
    return this.viewTypeOptions.find((view) => view.value === type);
  }

  getSavedViewMetadata = (id: string) => {
    const { view_name, view_description } =
      this.collection
        ?.get()
        .find((model) => `${model.get('id')}` === id)
        ?.get() || {};
    return { name: view_name, description: view_description };
  };

  /*
    Identifies whether swapping from one view type to another is allowed without modifying a query
  */
  isViewTypeCompatible = (
    fromViewType: DataViewType,
    toViewType: DataViewType,
    query: { get: (value: string) => string }
  ) => {
    const outsort = query.get('outsort');
    const metric = query.get('metric');
    const isAggTotal = outsort.startsWith('agg_total');
    const metricCount = metric.length;
    const allTypes = [...baseCompatibleTypes, ...additionalCompatibleTypes];
    const compatMap: Record<string, Readonly<DataViewType[]>> = {
      stackedArea: allTypes,
      stackedAreaHundredPercent: allTypes,
      line: allTypes,
      stackedBar: isAggTotal ? baseCompatibleTypes : allTypes,
      horizon: isAggTotal ? baseCompatibleTypes : allTypes,
      bar: isAggTotal ? baseCompatibleTypes : allTypes,
      pie: isAggTotal ? baseCompatibleTypes : allTypes,
      gauge: isAggTotal ? baseCompatibleTypes : allTypes,
      table: isAggTotal ? baseCompatibleTypes : allTypes,
      sunburst: isAggTotal && metricCount > 1 ? baseCompatibleTypes : allTypes,
      sankey: isAggTotal && metricCount > 1 ? baseCompatibleTypes : allTypes
    };
    const toTypes = compatMap[fromViewType];

    return toTypes && toTypes.includes(toViewType);
  };
}

const $dataviews = new DataViewStore();

export type DataViewStoreType = typeof $dataviews;

export default $dataviews;
